package fr.manyverse.app.presentation.components.premade

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Inventory2
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.presentation.components.CharacterAvatar
import fr.manyverse.app.presentation.components.ZoomableImageDialog
import fr.manyverse.app.presentation.components.player_screen.character.CharacterSheetReadOnly
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_story
import manyverse.shared.generated.resources.premade_inventory_section
import org.jetbrains.compose.resources.stringResource

/**
 * Carte d'affichage d'un personnage pré-tiré au design de référence (celui de `CharacterTab`) :
 * avatar + nom + histoire complète + fiche en lecture seule ([CharacterSheetReadOnly]).
 *
 * Partagée entre l'écran MJ ([fr.manyverse.app.presentation.screens.PreMadeCharactersScreen]) et le
 * dialog de choix côté joueur. Le slot [actions] permet d'ajouter des contrôles spécifiques (ex :
 * boutons éditer / supprimer du MJ) sous la fiche.
 */
@Composable
fun PreMadeCharacterCard(
    preMade: PreMadeCharacter,
    modifier: Modifier = Modifier,
    actions: (@Composable () -> Unit)? = null,
) {
    var showFullscreenImage by remember(preMade.id) { mutableStateOf(false) }

    Card(
        modifier = modifier
            .fillMaxSize()
            .padding(horizontal = 8.dp, vertical = 4.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            CharacterAvatar(
                imageUrl = preMade.signedImageUrl,
                characterName = preMade.name,
                size = 140.dp,
                onClick = if (preMade.signedImageUrl != null) {
                    { showFullscreenImage = true }
                } else null
            )

            Text(
                text = preMade.name,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )

            if (preMade.story.isNotBlank()) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        text = stringResource(Res.string.character_story),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = preMade.story,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            if (preMade.inventory.isNotEmpty()) {
                var inventoryExpanded by remember(preMade.id) { mutableStateOf(false) }
                OutlinedCard(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { inventoryExpanded = !inventoryExpanded },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.Inventory2,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(20.dp)
                            )
                            Text(
                                text = "${stringResource(Res.string.premade_inventory_section)} (${preMade.inventory.size})".uppercase(),
                                style = MaterialTheme.typography.labelLarge,
                                fontWeight = FontWeight.SemiBold,
                                letterSpacing = 1.5.sp,
                                color = MaterialTheme.colorScheme.primary
                            )
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .height(1.dp)
                                    .background(
                                        Brush.horizontalGradient(
                                            listOf(
                                                MaterialTheme.colorScheme.primary.copy(alpha = 0.3f),
                                                Color.Transparent
                                            )
                                        )
                                    )
                            )
                            Icon(
                                imageVector = if (inventoryExpanded) Icons.Default.KeyboardArrowUp
                                else Icons.Default.KeyboardArrowDown,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                        if (inventoryExpanded) {
                            preMade.inventory.forEach { item ->
                                PreMadeInventoryRow(
                                    name = item.name,
                                    category = item.category,
                                    quantity = item.quantity,
                                    signedImageUrl = item.signedImageUrl
                                )
                            }
                        }
                    }
                }
            }

            CharacterSheetReadOnly(
                sheet = preMade.sheet,
                modifier = Modifier.fillMaxWidth()
            )

            actions?.invoke()
        }
    }

    if (showFullscreenImage && preMade.signedImageUrl != null) {
        ZoomableImageDialog(
            imageUrl = preMade.signedImageUrl,
            contentDescription = preMade.name,
            onDismiss = { showFullscreenImage = false }
        )
    }
}
